import React from 'react';
import { useGetCart } from '../../../integrations/react_query/queries';
import { ECart, EQuerykeys } from '../../../models';
import { useCartAnalytics } from '../../../hooks/useCartAnalytics';
import { getSessionInfo } from '../../../utils/getSessionInfo';
import { formatAmount } from '../../../utils/funcs';
import CartSummary from '../../resuable-ui/cart-summary';
import CompleteTransaction from '../complete-transaction';
import PaymentButtons from '../payment-option/payment-buttons';
import { CustomerDetail } from '../payment-summary/customer-detail/index';

const MobilePaymentLayout = () => {
	const { data: cartItems, isLoading, isError } = useGetCart([EQuerykeys.cart, ECart.active]);
	const { cartLength, totalSavings, vatAmount, payableAmount, cartSumWithoutDiscount } = useCartAnalytics(
		cartItems?.products ?? []
	);
	const { companyInfo } = getSessionInfo();
	const VAT = companyInfo?.tax ?? 0;

	const totalSumOfAmountPaid = (cartItems?.payments || [])?.reduce((acc: any, p: any) => acc + +p?.amountPaid, 0);

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-screen">
				<div>Loading...</div>
			</div>
		);
	}

	if (isError || !cartItems || (cartItems?.products || []).length <= 0) {
		return (
			<div className="flex items-center justify-center h-screen">
				<div>No active cart found</div>
			</div>
		);
	}

	if (cartItems?.transactionId) {
		return (
			<CompleteTransaction
				data={cartItems}
				totalAmount={+payableAmount}
				totalPayment={totalSumOfAmountPaid}
				handlePrintReceipt={async () => {}}
				isPrinting={false}
			/>
		);
	}

	return (
		<div className="h-screen bg-white mt-[50px] flex flex-col">
			<div className="px-4">
				<CustomerDetail showCancelIcon={false} showEditIcon={false} />
			</div>

			<div className="sticky top-0 bg-white px-4 py-1 z-10">
				<h1 className="text-lg font-semibold text-gray-900">Summary</h1>
			</div>

			<div className="px-4 py-1 space-y-3 overflow-y-auto pb-[420px]">
				<div className="space-y-2">
					{(cartItems?.products ?? []).map(({ productName, sellingPrice, quantity }) => (
						<CartSummary key={productName} name={productName} amount={sellingPrice ?? 0} quantity={quantity} />
					))}
				</div>
			</div>

			<div className="fixed bottom-0 left-0 right-0 bg-white">
				<div className="border-t border-gray-200 px-4 py-4 space-y-2 bg-gray-50">
					<div className="flex justify-between items-center text-sm">
						<span className="text-gray-600">Subtotal</span>
						<span className="font-medium text-gray-900">{formatAmount(cartSumWithoutDiscount)}</span>
					</div>

					<div className="flex justify-between items-center text-sm">
						<span className="text-gray-600">Total Savings</span>
						<span className="font-medium text-gray-900">{formatAmount(totalSavings)}</span>
					</div>

					{VAT ? (
						<div className="flex justify-between items-center text-sm">
							<span className="text-gray-600">VAT ({VAT}%)</span>
							<span className="font-medium text-gray-900">{formatAmount(vatAmount)}</span>
						</div>
					) : null}

					<div className="flex justify-between items-center border-t border-gray-300 pt-2 mt-2">
						<span className="font-semibold text-gray-900">
							Total{' '}
							<span className="text-xs text-gray-500">
								({cartLength} item{cartLength !== 1 ? 's' : ''})
							</span>
						</span>
						<span className="text-lg font-bold text-gray-900">{formatAmount(payableAmount)}</span>
					</div>
				</div>

				<div className="border-t border-gray-200">
					<PaymentButtons />
				</div>
			</div>
		</div>
	);
};

export default MobilePaymentLayout;
